/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { TypescriptAuthSampleComponent } from './typescript-auth-sample/typescript-auth-sample.component';
import { JavascriptAuthSampleComponent } from './javascript-auth-sample/javascript-auth-sample.component';
import { AngularAuthSampleComponent } from './angular-auth-sample/angular-auth-sample.component';
import { BackendNodejsSampleComponent } from './backend-nodejs-sample/backend-nodejs-sample.component';

export interface CodeSample {
  id: string;
  title: string;
  subtitle: string;
  shortDescription: string;
  technologies: string[];
  category: 'frontend' | 'backend';
}

export interface SampleCategory {
  name: string;
  description: string;
  samples: CodeSample[];
}

@Component({
  selector: 'app-api-mesh-samples',
  standalone: true,
  imports: [CommonModule, TypescriptAuthSampleComponent, JavascriptAuthSampleComponent, AngularAuthSampleComponent, BackendNodejsSampleComponent],
  templateUrl: './api-mesh-samples.component.html',
  styleUrl: './api-mesh-samples.component.scss'
})
export class ApiMeshSamplesComponent implements OnInit, OnChanges {
  @Input() currentSection: string = 'samples';
  activeDetailPage: string | null = null;

  samples: CodeSample[] = [
    {
      id: 'typescript-auth',
      title: 'API Mesh Integration',
      subtitle: 'TypeScript on Frontend',
      shortDescription: 'Complete MSAL authentication workflow for direct access to API Mesh APIs using <b>TypeScript</b> from Frontend.',
      technologies: ['TypeScript', 'MSAL', 'Azure AD B2C', 'API Mesh API'],
      category: 'frontend'
    },
    {
      id: 'javascript-auth',
      title: 'API Mesh Integration',
      subtitle: 'JavaScript on Frontend',
      shortDescription: 'Complete MSAL authentication workflow for direct access to API Mesh APIs using <b>JavaScript</b> from Frontend.',
      technologies: ['JavaScript', 'MSAL', 'Azure AD B2C', 'API Mesh API'],
      category: 'frontend'
    },
    {
      id: 'angular-auth',
      title: 'API Mesh Integration',
      subtitle: 'Angular 19 on Frontend',
      shortDescription: 'Complete MSAL authentication workflow for direct access to API Mesh APIs using <b>Angular 19</b> from Frontend.',
      technologies: ['Angular 19', 'MSAL', 'Azure AD B2C', 'API Mesh API'],
      category: 'frontend'
    },
    {
      id: 'backend-nodejs',
      title: 'API Mesh Integration',
      subtitle: 'Node.js + TypeScript Backend',
      shortDescription: 'Service-to-service authentication using <b>Client Credentials Flow</b> for secure backend access to API Mesh APIs with <b>TypeScript on Node.js</b>.',
      technologies: ['Node.js', 'TypeScript', 'MSAL Node', 'Client Credentials'],
      category: 'backend'
    }
  ];

  sampleCategories: SampleCategory[] = [
    {
      name: 'Backend - API Mesh Integration',
      description: 'Server-side samples using Client Credentials Flow for secure service-to-service communication.',
      samples: this.samples.filter(s => s.category === 'backend')
    },
    {
      name: 'Frontend - API Mesh Integration',
      description: 'Interactive browser-based samples demonstrating user authentication and direct API access from frontend applications.',
      samples: this.samples.filter(s => s.category === 'frontend')
    }
  ];

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit() {
    // Check for sample query parameter from URL
    this.route.queryParams.subscribe(params => {
      if (params['sample']) {
        const sampleType = params['sample'];
        if (sampleType === 'typescript') {
          this.activeDetailPage = 'typescript-auth';
        } else if (sampleType === 'javascript') {
          this.activeDetailPage = 'javascript-auth';
        } else if (sampleType === 'angular') {
          this.activeDetailPage = 'angular-auth';
        } else if (sampleType === 'backend-nodejs') {
          this.activeDetailPage = 'backend-nodejs';
        }
      } else {
        this.updateDetailPageFromSection();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentSection']) {
      this.updateDetailPageFromSection();
    }
  }

  private updateDetailPageFromSection(): void {
    if (this.currentSection === 'sample-typescript') {
      this.activeDetailPage = 'typescript-auth';
    } else if (this.currentSection === 'sample-javascript') {
      this.activeDetailPage = 'javascript-auth';
    } else if (this.currentSection === 'sample-angular') {
      this.activeDetailPage = 'angular-auth';
    } else if (this.currentSection === 'sample-backend-nodejs') {
      this.activeDetailPage = 'backend-nodejs';
    } else {
      this.activeDetailPage = null;
    }
  }

  viewSampleDetails(sampleId: string) {
    this.activeDetailPage = sampleId;

    // Map sample ID to sample type for URL
    let sampleType = '';
    if (sampleId === 'typescript-auth') {
      sampleType = 'typescript';
    } else if (sampleId === 'javascript-auth') {
      sampleType = 'javascript';
    } else if (sampleId === 'angular-auth') {
      sampleType = 'angular';
    } else if (sampleId === 'backend-nodejs') {
      sampleType = 'backend-nodejs';
    }

    // Navigate to update URL with query parameter
    if (sampleType) {
      this.router.navigate(['/api-mesh/samples'], { queryParams: { sample: sampleType } });
    }
  }

  closeSampleDetails() {
    this.activeDetailPage = null;
    // Navigate to samples overview (without query parameter)
    this.router.navigate(['/api-mesh/samples']);
  }

  getComponentForSample(sampleId: string): any {
    switch (sampleId) {
      case 'typescript-auth':
        return TypescriptAuthSampleComponent;
      case 'javascript-auth':
        return JavascriptAuthSampleComponent;
      case 'angular-auth':
        return AngularAuthSampleComponent;
      case 'backend-nodejs':
        return BackendNodejsSampleComponent;
      default:
        return null;
    }
  }
}
